import type {
    FormFieldType,
    FormFieldSubType,
    FormField,
    ChoiceFormField,
    FormFieldOption,
    NumericFieldMetadata,
    TextFieldMetadata,
} from "~/models/FormField";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faAlignLeft,
    faCalendar,
    faCalendarAlt,
    faCaretDown,
    faCheckSquare,
    faClock,
    faDollarSign,
    faEnvelope,
    faFileAlt,
    faHashtag,
    faLink,
    faGauge,
    faPalette,
    faPhone,
    faQuestion,
    faArrowUpRightFromSquare,
    faSlidersH,
    faSmile,
    faPersonFromPortal,
    faStar,
    faSignalBars,
    faStickyNote,
    faThumbsUp,
    faToggleOn,
} from "@fortawesome/pro-solid-svg-icons";
import type { FieldDefinition } from "~/models/Automation";
import type { SimpleFieldDefinition } from "~/utils";
import { object, string, array, number, type InferType } from "yup";

type FieldTypes = FormFieldType | FormFieldSubType | "unknown";

const categories = {
    text: ["text-blue-300", "bg-blue-300", "text-gray-900"],
    contact: ["text-orange-300", "bg-orange-300", "text-gray-900"],
    temporal: ["text-cyan-300", "bg-cyan-300", "text-gray-900"],
    choice: ["text-purple-300", "bg-purple-300", "text-gray-900"],
    numeric: ["text-pink-300", "bg-pink-300", "text-gray-900"],
    rating: ["text-green-300", "bg-green-300", "text-gray-900"],
    other: ["text-yellow-300", "bg-yellow-300", "text-gray-900"],
    ending: ["text-gray-300", "bg-gray-300", "text-gray-900"],
};

export interface FormFieldTypeMap<T = FieldTypes> {
    type: T;
    icon: IconDefinition;
    category: keyof typeof categories;
    level: "primary" | "secondary";
    recommended: number;
    hidden: boolean;
    is_action: boolean;
    sub_types: (FormFieldSubType | "self")[];
    fields: (field: FormField) => FieldDefinition[];
    theme: {
        icon: string;
        background: string;
        foreground: string;
    };
    getLabel(): string;
}

function createFieldType(
    type: FieldTypes,
    icon: IconDefinition,
    category: keyof typeof categories,
    level: "primary" | "secondary",
    options: Partial<{
        recommended: number;
        hidden: boolean;
        is_action: boolean;
        fields: (this: FormFieldTypeMap, field: FormField) => FieldDefinition[];
        sub_types: (FormFieldSubType | "self")[];
    }> = {},
): FormFieldTypeMap {
    return {
        type,
        icon,
        category,
        level,
        fields(field: FormField): FieldDefinition[] {
            return (
                options?.fields?.call(this, field) ??
                makeFieldTypeCollection(this, field)
            );
        },
        hidden: !!options?.hidden,
        recommended: options?.recommended ?? 0,
        sub_types: options?.sub_types ?? [],
        is_action: options?.is_action ?? false,
        theme: {
            icon: categories[category][0],
            background: categories[category][1],
            foreground: categories[category][2],
        },
        getLabel() {
            const { t } = useI18n();

            return t(`enums.form_field_type.${type}`);
        },
    };
}

function makeFieldTypeCollection(
    map: FormFieldTypeMap,
    field: FormField,
    fields: SimpleFieldDefinition<any>[] = [],
): FieldDefinition<any, FormFieldTypeMap>[] {
    return (
        createFieldCollection()
            .addSimple<any>(
                {
                    label: field.label ?? `(${map.getLabel()})`,
                    key: "form.",
                    type: "string",
                    default_value: null,
                },
                ...fields,
            )
            .all() as FieldDefinition<unknown, FormFieldTypeMap>[]
    ).map((simpleField) => {
        simpleField.metadata = {
            ...simpleField.metadata,
            position: field.position,
            type: field.type,
            sub_type: field.sub_type,
            icon: map.icon,
            theme: map.theme,
        };
        return simpleField;
    });
}

const formFieldTypes = [
    // Text
    createFieldType("text", faAlignLeft, "text", "primary", {
        recommended: 2,
        sub_types: ["self", "memo"],
    }),
    createFieldType("memo", faStickyNote, "text", "secondary", {
        recommended: 4,
    }),
    // Contact
    createFieldType("email", faEnvelope, "contact", "primary", {
        recommended: 5,
    }),
    createFieldType("phone", faPhone, "contact", "primary", {
        recommended: 6,
    }),
    createFieldType("url", faLink, "contact", "primary"),
    // Temporal
    createFieldType("date", faCalendar, "temporal", "primary"),
    createFieldType("time", faClock, "temporal", "primary"),
    createFieldType("datetime", faCalendarAlt, "temporal", "primary"),
    // Choice
    createFieldType("binary", faToggleOn, "choice", "primary", {
        sub_types: ["checkbox", "toggle"],
    }),
    createFieldType("checkbox", faCheckSquare, "choice", "secondary", {
        hidden: true,
    }),
    createFieldType("toggle", faToggleOn, "choice", "secondary", {
        hidden: true,
    }),
    createFieldType("choice", faCheckSquare, "choice", "primary", {
        recommended: 1,
        sub_types: ["dropdown", "list"],
        hidden: true,
    }),
    createFieldType("list", faCheckSquare, "choice", "secondary", {
        recommended: 1,
    }),
    createFieldType("dropdown", faCaretDown, "choice", "secondary"),
    // Numeric
    createFieldType("number", faHashtag, "numeric", "primary"),
    createFieldType("currency", faDollarSign, "numeric", "primary"),
    createFieldType("range", faSlidersH, "numeric", "primary", {
        hidden: true,
    }),
    // Rating
    createFieldType("nps", faGauge, "rating", "secondary", {
        recommended: 3,
    }),
    createFieldType("rating", faStar, "rating", "primary", {
        hidden: true,
        sub_types: [
            // "star",
            // "thumbs",
            // "emoji",
            // "slider",
            "numeric",
            "nps",
        ],
    }),
    createFieldType("star", faStar, "rating", "secondary", {
        hidden: true,
    }),
    createFieldType("numeric", faSignalBars, "rating", "secondary", {}),
    createFieldType("thumbs", faThumbsUp, "rating", "secondary", {
        hidden: true,
    }),
    createFieldType("emoji", faSmile, "rating", "secondary", {
        hidden: true,
    }),
    createFieldType("slider", faSlidersH, "rating", "secondary", {
        hidden: true,
    }),
    // Other
    createFieldType("file", faFileAlt, "other", "primary"),
    createFieldType("ending", faPersonFromPortal, "ending", "primary", {
        sub_types: ["self", "redirect"],
    }),
    createFieldType(
        "redirect",
        faArrowUpRightFromSquare,
        "ending",
        "secondary",
        {
            is_action: true,
        },
    ),
    createFieldType("color", faPalette, "other", "primary", {
        hidden: true,
    }),
    createFieldType("unknown", faQuestion, "ending", "primary", {
        hidden: true,
    }),
];


const schema = object({
    name: string().nullable().min(0).max(255),
    description: string().nullable().min(0).max(1024),
    fields: array().of(
        object({
            position: number().required().min(0),
            label: string().nullable().min(0).max(255),
            description: string().nullable().min(0).max(1024),
            options: array()
                .nullable()
                .of(
                    object({
                        position: number().nullable().min(0),
                        label: string().nullable().min(0).max(255),
                        value: string().nullable().min(0).max(255),
                    }),
                ),
        }),
    ),
});

export type FormSchema = InferType<typeof schema>;

const formFields = {
    schema,
    types: formFieldTypes,
    find(type: FieldTypes) {
        return this.types.find((map) => map.type === type);
    },
    getPrimaryFields(): FormFieldTypeMap<FormFieldType>[] {
        return this.types.filter(
            (type) => type.level === "primary",
        ) as FormFieldTypeMap<FormFieldType>[];
    },
    getSecondaryFields(): FormFieldTypeMap<FormFieldSubType>[] {
        return this.types.filter(
            (type) => type.level === "secondary",
        ) as FormFieldTypeMap<FormFieldSubType>[];
    },
    getSecondaryFieldsFor(type: FormFieldType): FormFieldTypeMap<FieldTypes>[] {
        const map = this.types.find((map) => map.type === type);

        if (!map) {
            return [];
        }

        const types = this.types.filter((type) =>
            map.sub_types.includes(type.type as FormFieldSubType),
        ) as FormFieldTypeMap<FieldTypes>[];

        if (map.sub_types.includes("self")) {
            types.unshift(map);
        }

        return types;
    },
    getPrimaryFieldFor(type: FormFieldType) {
        return this.types.find(
            (item) =>
                (item.type === type && item.level === "primary") ||
                item.sub_types.includes(type),
        );
    },
    makeFormField(
        type: FormFieldType,
        sub_type: FormFieldSubType = null,
    ): Omit<FormField, "created_at" | "updated_at"> & {
        options: FormFieldOption[] | null;
    } {
        let metadata = {};
        let options: ChoiceFormField["options"] = [];

        if (["text", "email", "phone", "url"].includes(type)) {
            metadata = {
                placeholder: null,
                validation: {
                    min: null,
                    max: null,
                    pattern: null,
                },
            } as TextFieldMetadata;

            if (type !== "text" || ![null, "memo"].includes(sub_type)) {
                sub_type = null;
            }
        } else if (["number", "range"].includes(type)) {
            metadata = {
                placeholder: null,
                validation: {
                    min: null,
                    max: null,
                    step: null,
                },
            } as NumericFieldMetadata;

            sub_type = null;
        } else if (type === "binary") {
            if (!["checkbox", "toggle"].includes(sub_type as string)) {
                sub_type = "checkbox";
            }
        } else if (type === "choice") {
            metadata = {
                placeholder: null,
                multiple: false,
            };

            if (!["dropdown", "list"].includes(sub_type as string)) {
                sub_type = "dropdown";
            }

            options = [
                {
                    id: createUuid(),
                    position: 0,
                    label: "",
                    value: "",
                },
                {
                    id: createUuid(),
                    position: 1,
                    label: "",
                    value: "",
                },
            ];
        } else if (type === "rating") {
            metadata = {
                min_value: sub_type === "nps" ? 0 : 1,
                max_value: 10,
                min_label: "",
                max_label: "",
            };

            if (
                !["star", "numeric", "nps", "thumbs", "emoji", "slider"].includes(
                    sub_type as string,
                )
            ) {
                sub_type = "numeric";
            }
        } else if (type === "file") {
            metadata = {
                multiple: false,
                accept: null,
            };

            sub_type = null;
        } else if (type === "ending") {
            metadata = {
                button_enabled: false,
                button_text: null,
                redirect_url: null,
                timer_enabled: false,
                timer: 0,
            };

            if (!["redirect"].includes(sub_type)) {
                sub_type = null;
            }
        } else {
            sub_type = null;
        }

        return {
            id: `${createUuid()}`,
            type,
            sub_type: sub_type as FormField["sub_type"],
            position: 0,
            label: "",
            slug: "",
            description: "",
            required: false,
            hidden: false,
            layout: {
                media: null,
            },
            logic: [],
            metadata,
            options,
        };
    }
};

export default formFields;
